// The parts of a single page (DESIGN.md «Запись», um/2026-10-30/03 п.8): the top line with «назад» and the path, the title, the list of
// properties and a block of related records — the record's own page, the file's page and the letter are drawn with the same parts.

import type { ReactNode } from 'react';
import { navigate, wantsNewTab } from '../shell/router.ts';
import { Icon } from '../ui/icons.tsx';
import { cx } from '../ui/primitives.tsx';
import { singleClass } from '../ui/Page.tsx';
import { TopBar } from '../ui/TopBar.tsx';
import { Path, type Crumb } from './RecordPath.tsx';
import s from './record.module.css';

/** A single page: the core's top line (back and path, actions) and the scrolled body of the single width. */
export function Single({ crumbs, onBack, actions, children, label }: {
  crumbs?: Crumb[]; onBack?: () => void; actions?: ReactNode; children: ReactNode; label?: string;
}) {
  return (
    <div className={s.view} aria-label={label}>
      <TopBar start={crumbs && onBack ? <Path crumbs={crumbs} onBack={onBack} /> : undefined} end={actions} />
      <div className={s.scroll}><div className={singleClass}>{children}</div></div>
    </div>
  );
}

/** The title of a single page, as the record's title: its H1, or H2 in the reading column under a list (the letter). */
export function SingleTitle({ children, after, level = 1 }: { children: ReactNode; after?: ReactNode; level?: 1 | 2 }) {
  const Tag = level === 1 ? 'h1' : 'h2';
  return <Tag className={cx(s.title, s.titleStatic)}>{children}{after}</Tag>;
}

/** The row of the page's actions under the title (the record's actions). */
export const SingleActions = ({ children }: { children: ReactNode }) => <div className={s.actions}>{children}</div>;

/** Properties that are read, not edited (a file's size, a letter's sender): the same grid, icons and rows as a record's properties. */
export function InfoList({ rows }: { rows: { icon: string; label: string; value: ReactNode }[] }) {
  return (
    <dl className={s.props}>
      {rows.map((r) => (
        <div key={r.label} className={s.propRow}>
          <dt className={s.label}><Icon name={r.icon} size={14} />{r.label}</dt>
          <dd className={cx(s.value, s.valueLocked)}>{r.value ?? <span className={s.empty}>Пусто</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface RelatedItem { key: string; icon: string; title: string; sub?: string; href?: string }

/** A block of related records under the properties, as a record's relation: a heading with the count and rows that open the record. */
export function RelatedBlock({ title, items, empty }: { title: string; items: RelatedItem[]; empty?: string }) {
  return (
    <section className={s.block}>
      <h2 className={s.blockTitle}>{title}<span className={s.count}>{items.length || ''}</span></h2>
      {items.length ? (
        <ul className={s.related}>
          {items.map((it) => (
            <li key={it.key} className={cx(s.relItem, !it.href && s.relStatic)}>
              <Icon name={it.icon} size={14} />
              {/* The row's link is a real link: it opens by the keyboard and a screen reader names it (review P3). */}
              <span className={s.relTitle}>{it.href ? <a href={it.href} className={s.relLink} onClick={(e) => { if (wantsNewTab(e)) return; e.preventDefault(); navigate(it.href!); }}>{it.title}</a> : it.title}</span>
              {it.sub && <span className={s.relSub}>{it.sub}</span>}
            </li>
          ))}
        </ul>
      ) : <div className={s.empty}>{empty ?? 'Нет связанных записей'}</div>}
    </section>
  );
}
